import type { GridColDef } from '@mui/x-data-grid-pro';
import { TagBadge } from '@nexelem/components';
import { displayText, FormattedDateBox, formatters, LongStringContainer } from '@nexelem/utils';
import { formatPhoneNumber } from '../formatPhoneNumber';
import { AvailableColumnTypes, GenericRenderer } from './enum';

/**
 * Generic helper to safely access config properties from colDef.
 */
const getConfig = <T extends Record<string, unknown> = Record<string, unknown>>(obj: unknown): T | undefined => {
  if (
    typeof obj === 'object' &&
    obj !== null &&
    'config' in obj &&
    typeof obj.config === 'object' &&
    obj.config !== null
  ) {
    return obj.config as T;
  }
  return undefined;
};

const getProperty = <T = unknown,>(obj: unknown, key: string): T | undefined => {
  if (typeof obj === 'object' && obj !== null && key in obj) {
    return (obj as Record<string, unknown>)[key] as T;
  }
  return undefined;
};

type RendererConfig = Pick<Partial<GridColDef>, 'renderCell' | 'valueFormatter' | 'valueGetter'>;

export const renderMap: Record<keyof Omit<GenericRenderer, 'check'>, RendererConfig> = {
  [AvailableColumnTypes.string]: {
    renderCell: ({ value, colDef }) => {
      const config = getConfig<{ withDisplayText?: boolean }>(colDef);
      return config?.withDisplayText ? displayText(value) : value;
    },
  },
  [AvailableColumnTypes.longString]: {
    renderCell: ({ value }) => <LongStringContainer>{displayText(value)}</LongStringContainer>,
  },
  [AvailableColumnTypes.formattedTime]: {
    renderCell: ({ value, colDef }) => {
      const config = getConfig<{ displayWithHours?: boolean }>(colDef);
      return <FormattedDateBox displayWithHours={config?.displayWithHours} value={value} />;
    },
  },
  [AvailableColumnTypes.badges]: {
    renderCell: ({ value, row, colDef }) => {
      if (Array.isArray(value) && value.length) {
        const mapper = getProperty<(element: typeof row) => string[]>(colDef, 'mapper');
        return <TagBadge isExpanded={row.selected} values={mapper ? mapper(row) : value} />;
      }
      return null;
    },
  },
  [AvailableColumnTypes.currency]: {
    valueGetter: (value) => {
      try {
        return formatters.number.format(value || 0, { maximumFractionDigits: 2, minimumFractionDigits: 2 });
      } catch (e) {
        return ((value || 0) as number)?.toFixed(2).replace('.', ',');
      }
    },
  },
  [AvailableColumnTypes.phoneNumber]: {
    valueFormatter: formatPhoneNumber,
  },
  [AvailableColumnTypes.percentage]: {
    valueGetter: (value) => ((parseFloat(value) || 0) * 100).toFixed(0) + '%',
  },
  [AvailableColumnTypes.number]: {
    renderCell: ({ value, colDef }) => {
      const config = getConfig<{ zeroAsEmpty?: boolean; withDisplayText?: boolean }>(colDef);

      if (config?.zeroAsEmpty && !value) {
        return '-';
      }
      return config?.withDisplayText ? formatters.number.formatWithDisplayText(value) : formatters.number.format(value);
    },
  },
};
